import { DataProvider } from 'react-admin';
import { Api } from '@/generated/Api';
import { config } from '@/config/config';
import { authService } from '@/services/authService';

// imports de orval
import {
  // Funciones Orval para devices
  getApiVariablesDevices,
  postApiVariablesDevice,
  putApiVariablesDevice,
  deleteApiVariablesDevice,
  getApiRole,
  getApiVariablesTasks,
  getApiSubscriptionSources,
  getApiSubscriptionSourcesId,
  postApiSubscriptionSources,
  putApiSubscriptionSourcesId,
  deleteApiSubscriptionSourcesId,
  // Funciones Orval para variables SCADA
  postApiVariablesScada,
  // Futuras importaciones de orval...
} from '@/api/gen/api';
const api = new Api({
  baseURL: config.apiUrl,
});

let isRedirectingToLogin = false;
api.instance.interceptors.request.use(cfg => {
  const token = authService.getAccessToken();
  console.log(
    '🔐 Token obtenido para request:',
    token ? `${token.substring(0, 20)}...` : 'NO HAY TOKEN'
  );

  if (token) {
    cfg.headers.Authorization = `Bearer ${token}`;
    console.log(
      '✅ Token agregado a headers:',
      cfg.headers.Authorization.substring(0, 30) + '...'
    );
  } else {
    console.warn('⚠️ No se encontró token en localStorage');
  }

  console.log('📤 Request config:', {
    url: cfg.url,
    method: cfg.method,
    headers: cfg.headers,
  });

  return cfg;
});

api.instance.interceptors.response.use(
  response => {
    console.log('✅ Response exitoso:', {
      status: response.status,
      url: response.config.url,
      data: response.data ? 'DATA PRESENT' : 'NO DATA',
    });
    return response;
  },
  error => {
    const status: number | undefined = error.response?.status;
    const url = error.config?.url;

    console.log('❌ Response error:', {
      status: status,
      url: url,
      message: error.message,
      hasToken: !!authService.getAccessToken(),
    });

    // error 401
    if (status === 401) {
      const token = authService.getAccessToken();
      console.error('🚨 ERROR 401 DETECTADO:', {
        requestUrl: url,
        requestMethod: error.config?.method,
        tokenInStorage: token ? 'PRESENT' : 'MISSING',
        tokenValue: token ? `${token.substring(0, 20)}...` : 'NONE',
      });
    }

    if (status && !error.status) {
      error.status = status;
    }

    if ((status === 401 || status === 403) && !isRedirectingToLogin) {
      isRedirectingToLogin = true;
      console.log('🔄 Interceptor: Token inválido detectado');

      const refreshToken = authService.getAccessToken();
      if (refreshToken && status === 401) {
        console.log('🔄 Intentando refresh token...');
        // TODO: Implementar refresh token logic
      }

      // Redirección al login
      // location.href = '/login';
      setTimeout(() => (isRedirectingToLogin = false), 1500);
    }
    return Promise.reject(error);
  }
);

export const dataProvider: DataProvider = {
  getList: async (resource, params) => {
    console.log(`📦 DataProvider: getList ${resource}`);
    console.log('🔐 Token actual:', authService.getAccessToken());
    const { page = 1, perPage = 10 } = params.pagination || {
      page: 1,
      perPage: 10,
    };
    const { field = 'id', order = 'ASC' } = params.sort || {
      field: 'id',
      order: 'ASC',
    };
    const { q, ...filters } = params.filter || {};

    try {
      let response: any;

      switch (resource) {
        case 'inputs': {
          const inputsParams: any = {
            page,
            pageSize: perPage,
            isPaginated: true,
            ...(q ? { SearchString: q } : {}),

            ...(filters.plantId ? { PlantId: Number(filters.plantId) } : {}),
            ...(filters.zoneId ? { ZoneId: Number(filters.zoneId) } : {}),
            ...(filters.tableId ? { TableId: Number(filters.tableId) } : {}),
          };

          console.log(
            '🎯 [DataProvider] Parámetros para inputs:',
            inputsParams
          );

          response = await api.variablesInputsList(inputsParams);

          let items = [];
          let totalCount = 0;
          if (response.data?.data?.items) {
            items = response.data.data.items;
            totalCount = response.data.data.totalCount || 0;
          } else if (response.data?.items) {
            items = response.data.items;
            totalCount = response.data.totalCount || 0;
          } else if (Array.isArray(response.data)) {
            items = response.data;
            totalCount = items.length;
          }
          return { data: items, total: totalCount };
        }
        case 'devices': {
          //  Orval para devices
          const orvalParams = {
            isPaginated: true,
            page: page,
            pageSize: perPage,
            // Mantener compatibilidad con búsquedas
            ...(q ? { SearchString: q } : {}),
            // filtros
            ...(filters.plantId ? { PlantId: Number(filters.plantId) } : {}),
            ...(filters.zoneId ? { ZoneId: Number(filters.zoneId) } : {}),
            ...(filters.tableId ? { TableId: Number(filters.tableId) } : {}),
          };

          console.log('🎯 Orval - Parámetros para devices:', orvalParams);

          try {
            const orvalResponse = await getApiVariablesDevices(orvalParams);

            console.log('🎯 Orval - Respuesta recibida:', orvalResponse);

            let items = [];
            let totalCount = 0;

            // estructura de respuesta de Orval
            if (orvalResponse && orvalResponse.data) {
              // Orval generalmente devuelve { data: { items: [], totalCount: number } }
              const responseData = orvalResponse.data;

              if (responseData.items && Array.isArray(responseData.items)) {
                items = responseData.items;
                totalCount =
                  responseData.totalCount || responseData.items.length;
              } else if (Array.isArray(responseData)) {
                items = responseData;
                totalCount = responseData.length;
              } else {
                // Fallback: tratar la respuesta completa como un item
                items = [responseData];
                totalCount = 1;
              }
            } else {
              // Fallback si Orval devuelve estructura diferente
              items = orvalResponse.items ||
                orvalResponse.data || [orvalResponse];
              totalCount = orvalResponse.totalCount || items.length;
            }
            const mappedItems = items.map((item: any) => ({
              id: item.id,
              variableName: item.variableName,
              deviceId: item.deviceId,
              deviceType: item.deviceType,
              ipAddress: item.ipAddress,
              tableId: item.tableId ?? item.table?.id,
              tableName: item.tableName ?? item.table?.tableName,
              plantId:
                item.plantId ?? item.table?.zone?.plantId ?? item.plant?.id,
            }));

            console.log('🎯 Orval - Items mapeados:', mappedItems.length);

            return { data: mappedItems, total: totalCount };
          } catch (orvalError) {
            console.error('❌ Orval - Error en getList devices:', orvalError);
            console.log('🔄 Fallback a implementación original...');
            const fallbackResponse = await api.variablesDevicesList({
              page,
              pageSize: perPage,
            });

            let fallbackItems = [];
            let fallbackTotal = 0;

            if (fallbackResponse.data?.data?.items) {
              fallbackItems = fallbackResponse.data.data.items;
              fallbackTotal = fallbackResponse.data.data.totalCount || 0;
            } else if (fallbackResponse.data?.items) {
              fallbackItems = fallbackResponse.data.items;
              fallbackTotal = fallbackResponse.data.totalCount || 0;
            } else if (Array.isArray(fallbackResponse.data)) {
              fallbackItems = fallbackResponse.data;
              fallbackTotal = fallbackItems.length;
            }

            const mappedFallbackItems = fallbackItems.map((item: any) => ({
              id: item.id,
              variableName: item.variableName,
              deviceId: item.deviceId,
              deviceType: item.deviceType,
              ipAddress: item.ipAddress,
              tableId: item.tableId ?? item.table?.id,
              tableName: item.tableName ?? item.table?.tableName,
              plantId:
                item.plantId ?? item.table?.zone?.plantId ?? item.plant?.id,
            }));

            return { data: mappedFallbackItems, total: fallbackTotal };
          }
        }
        case 'users':
          const usersParams: any = {
            page,
            pageSize: perPage,
            isPaginated: true,
          };

          if (q && q.trim() !== '') {
            usersParams.SearchString = q;
          }

          response = await api.usersList(usersParams);
          break;
        case 'roles':
          const rolesParams: any = {
            page,
            pageSize: perPage,
            isPaginated: true,
          };

          if (q && q.trim() !== '') {
            rolesParams.SearchString = q;
          }

          if (filters.plantId) {
            rolesParams.PlantId = Number(filters.plantId);
          }

          response = await getApiRole(rolesParams);
          // getApiRole devuelve la estructura directamente, no envuelta en .data
          if (response && response.items) {
            return {
              data: response.items,
              total: response.totalCount || 0,
            };
          }
          break;
        case 'clients':
          response = await api.clientsList({
            page,
            pageSize: perPage,
            isPaginated: true,
            SearchString: q,
          });
          break;
        case 'plants':
          const plantsParams: any = {
            page,
            pageSize: perPage,
            isPaginated: true,
            SearchString: q,
          };
          if (filters.clientId) {
            plantsParams.ClientId = Number(filters.clientId);
          }
          response = await api.plantsList(plantsParams as any);
          break;
        case 'dashboardgroups':
          if (filters.plantId) {
            const plantIdNum = Number(filters.plantId);
            try {
              const axiosResp = await api.instance.request({
                url: `/api/DashboardGroups/ByPlant`,
                method: 'GET',
                params: { id: plantIdNum },
              });
              const raw = axiosResp.data;
              const dataArr = Array.isArray(raw)
                ? raw
                : Array.isArray(raw?.data?.items)
                  ? raw.data.items
                  : [];

              // Retorno directo con paginación local
              return {
                data: dataArr,
                total: dataArr.length,
              };
            } catch (e) {
              console.error('Error fetching dashboard groups by plant:', e);
              throw e;
            }
          } else {
            response = await api.dashboardGroupsList({
              page,
              pageSize: perPage,
              isPaginated: true,
              SearchString: q,
            });
          }
          break;
        case 'dashboards':
          if (filters.dashboardGroupId) {
            response = await api.dashboardsByDashboardGroupDetail(
              Number(filters.dashboardGroupId)
            );
            const data = Array.isArray(response.data) ? response.data : [];

            // Retorno directo para casos especiales
            return {
              data: data,
              total: data.length,
            };
          } else {
            response = await api.dashboardsList({
              page,
              pageSize: perPage,
              isPaginated: true,
              SearchString: q,
            });
          }
          break;
        case 'diagrams':
          if (filters.plantId) {
            response = await api.diagramsByPlantDetail(Number(filters.plantId));
            const data = Array.isArray(response.data) ? response.data : [];

            // Retorno directo para casos especiales
            return {
              data: data,
              total: data.length,
            };
          } else {
            const queryParams: any = {
              page,
              pageSize: perPage,
              isPaginated: true,
              include: 'nodes,edges',
            };
            if (q) {
              queryParams.search = q;
            }
            Object.keys(filters).forEach(key => {
              if (filters[key] !== undefined && filters[key] !== null) {
                queryParams[key] = filters[key];
              }
            });
            if (field && order) {
              queryParams.ordering = order === 'DESC' ? `-${field}` : field;
            }
            response = await api.diagramsList(queryParams);
          }
          break;
        case 'alerts':
          if (filters.zoneId) {
            response = await api.alertsByZoneDetail(Number(filters.zoneId));
            const data = Array.isArray(response.data) ? response.data : [];

            // Retorno directo para casos especiales
            return {
              data: data,
              total: data.length,
            };
          } else if (filters.plantId) {
            response = await api.alertsByPlantDetail(Number(filters.plantId));
            const data = Array.isArray(response.data) ? response.data : [];

            // Retorno directo para casos especiales
            return {
              data: data,
              total: data.length,
            };
          } else {
            response = await api.alertsList({
              page,
              pageSize: perPage,
              SearchString: q,
            });
          }
          break;
        case 'variables':
          if (filters.tableId) {
            console.log(
              '🔍 Loading variables by table (SCADA + TASK) con paginación servidor SCADA:',
              {
                tableId: filters.tableId,
                page,
                perPage,
              }
            );

            const [scadaResponse, taskResponse] = await Promise.allSettled([
              api.instance.request({
                url: `/api/Variables/scada/by-table/${filters.tableId}`,
                method: 'GET',
                params: {
                  page,
                  pageSize: perPage,
                  isPaginated: true,
                },
              }),
              // TASK mantiene paginación client-side
              api.variablesTaskByTableDetail(Number(filters.tableId)),
            ]);

            console.log(
              '🔍 SCADA by-table Response status ASDASD:',
              scadaResponse.status
            );
            if (scadaResponse.status === 'fulfilled') {
              console.log(
                '🔍 SCADA by-table Response data:',
                scadaResponse.value.data
              );
            }

            let scadaData = [];
            let scadaTotal = 0;
            let taskData = [];

            // Procesar respuesta SCADA con paginación servidor
            if (scadaResponse.status === 'fulfilled') {
              const scadaRaw = scadaResponse.value.data;
              console.log('🔍 SCADA by-table RAW RESPONSE:', scadaRaw);

              // ESTRUCTURA: { totalCount: number, page: number, pageSize: number, items: [] }
              if (
                scadaRaw &&
                scadaRaw.items &&
                scadaRaw.totalCount !== undefined
              ) {
                scadaData = scadaRaw.items;
                scadaTotal = scadaRaw.totalCount;
                console.log('✅ SCADA by-table: Usando paginación servidor', {
                  items: scadaData.length,
                  total: scadaTotal,
                });
              } else if (Array.isArray(scadaRaw)) {
                // Fallback: paginación client-side si no viene estructurado
                scadaData = scadaRaw;
                scadaTotal = scadaRaw.length;
                console.log(
                  '⚠️ SCADA by-table: Fallback a paginación client-side',
                  { items: scadaData.length }
                );
              } else {
                console.warn(
                  '❌ SCADA by-table: Estructura de respuesta no reconocida',
                  scadaRaw
                );
                scadaData = [];
                scadaTotal = 0;
              }
            }

            // Procesar TASK (paginación client-side)
            if (
              taskResponse.status === 'fulfilled' &&
              Array.isArray(taskResponse.value.data)
            ) {
              taskData = taskResponse.value.data;
              console.log('🔍 TASK by-table data:', { items: taskData.length });
            }

            const combinedData = [...scadaData, ...taskData];

            console.log('🔍 FINAL RESULT by-table:', {
              scadaData: scadaData.length,
              scadaTotal,
              taskData: taskData.length,
              combinedData: combinedData.length,
              total: scadaTotal + taskData.length,
            });

            return {
              data: combinedData,
              total: scadaTotal + taskData.length,
            };
          } else if (filters.zoneId) {
            console.log(
              '🔍 Loading variables by zone (SCADA + TASK) con paginación servidor SCADA:',
              {
                zoneId: filters.zoneId,
                page,
                perPage,
              }
            );

            try {
              const [scadaResponse, taskResponse] = await Promise.allSettled([
                api.instance.request({
                  url: `/api/Variables/scada/by-zone/${filters.zoneId}`,
                  method: 'GET',
                  params: {
                    page,
                    pageSize: perPage,
                    isPaginated: true,
                  },
                }),
                // TASK mantiene paginación client-side
                api.variablesTaskByZoneDetail(Number(filters.zoneId)),
              ]);

              console.log(
                '🔍 SCADA by-zone Response status:',
                scadaResponse.status
              );
              if (scadaResponse.status === 'fulfilled') {
                console.log(
                  '🔍 SCADA by-zone Response data:',
                  scadaResponse.value.data
                );
              }

              let scadaData = [];
              let scadaTotal = 0;
              let taskData = [];

              // Procesar SCADA con paginación servidor
              if (scadaResponse.status === 'fulfilled') {
                const scadaRaw = scadaResponse.value.data;
                console.log('🔍 SCADA by-zone RAW RESPONSE:', scadaRaw);

                if (
                  scadaRaw &&
                  scadaRaw.items &&
                  scadaRaw.totalCount !== undefined
                ) {
                  scadaData = scadaRaw.items;
                  scadaTotal = scadaRaw.totalCount;
                  console.log('✅ SCADA by-zone: Usando paginación servidor', {
                    items: scadaData.length,
                    total: scadaTotal,
                  });
                } else if (Array.isArray(scadaRaw)) {
                  scadaData = scadaRaw;
                  scadaTotal = scadaRaw.length;
                  console.log(
                    '⚠️ SCADA by-zone: Fallback a paginación client-side',
                    { items: scadaData.length }
                  );
                } else {
                  console.warn(
                    '❌ SCADA by-zone: Estructura de respuesta no reconocida',
                    scadaRaw
                  );
                  scadaData = [];
                  scadaTotal = 0;
                }
              }

              // Procesar TASK (paginación client-side)
              if (
                taskResponse.status === 'fulfilled' &&
                Array.isArray(taskResponse.value.data)
              ) {
                taskData = taskResponse.value.data;
                console.log('🔍 TASK by-zone data:', {
                  items: taskData.length,
                });
              }

              const combinedData = [...scadaData, ...taskData];

              console.log('🔍 FINAL RESULT by-zone:', {
                scadaData: scadaData.length,
                scadaTotal,
                taskData: taskData.length,
              });

              return {
                data: combinedData,
                total: scadaTotal + taskData.length,
              };
            } catch (error) {
              console.error('🔍 ERROR Zone Filter:', error);
              throw error;
            }
          } else if (filters.plantId) {
            console.log(
              '🔍 Loading variables by plant (SCADA + TASK) con paginación servidor SCADA:',
              {
                plantId: filters.plantId,
                page,
                perPage,
              }
            );

            try {
              const [scadaResponse, taskResponse] = await Promise.allSettled([
                api.instance.request({
                  url: `/api/Variables/scada/by-plant/${filters.plantId}`,
                  method: 'GET',
                  params: {
                    page,
                    pageSize: perPage,
                    isPaginated: true,
                  },
                }),
                // TASK paginacion cliente
                api.variablesTaskByPlantDetail(Number(filters.plantId)),
              ]);

              console.log(
                '🔍 SCADA by-plant Response status:',
                scadaResponse.status
              );
              if (scadaResponse.status === 'fulfilled') {
                console.log(
                  '🔍 SCADA by-plant Response data:',
                  scadaResponse.value.data
                );
              }

              let scadaData = [];
              let scadaTotal = 0;
              let taskData = [];

              // Procesar SCADA con paginación servidor
              if (scadaResponse.status === 'fulfilled') {
                const scadaRaw = scadaResponse.value.data;
                console.log('🔍 SCADA by-plant RAW RESPONSE:', scadaRaw);

                // ESTRUCTURA: { totalCount: number, page: number, pageSize: number, items: [] }
                if (
                  scadaRaw &&
                  scadaRaw.items &&
                  scadaRaw.totalCount !== undefined
                ) {
                  scadaData = scadaRaw.items;
                  scadaTotal = scadaRaw.totalCount;
                  console.log('✅ SCADA by-plant: Usando paginación servidor', {
                    items: scadaData.length,
                    total: scadaTotal,
                  });
                } else if (Array.isArray(scadaRaw)) {
                  scadaData = scadaRaw;
                  scadaTotal = scadaRaw.length;
                  console.log(
                    '⚠️ SCADA by-plant: Fallback a paginación client-side',
                    { items: scadaData.length }
                  );
                } else {
                  console.warn(
                    '❌ SCADA by-plant: Estructura de respuesta no reconocida',
                    scadaRaw
                  );
                  scadaData = [];
                  scadaTotal = 0;
                }
              }

              // Procesar TASK (paginación client-side)
              if (taskResponse.status === 'fulfilled') {
                const taskRaw = taskResponse.value.data;
                console.log('🔍 TASK by-plant raw response:', taskRaw);

                let taskItems = [];

                if (Array.isArray(taskRaw)) {
                  taskItems = taskRaw;
                } else if (taskRaw && Array.isArray(taskRaw.data)) {
                  taskItems = taskRaw.data;
                } else if (taskRaw && Array.isArray(taskRaw.items)) {
                  taskItems = taskRaw.items;
                } else if (
                  taskRaw?.data?.items &&
                  Array.isArray(taskRaw.data.items)
                ) {
                  taskItems = taskRaw.data.items;
                }

                taskData = taskItems.map((item: any) => ({
                  ...item,
                  variableName:
                    item.variableName ||
                    item.name ||
                    item.varName ||
                    item.tag ||
                    `TASK_${item.id}`,
                  tableId: item.tableId,
                  description: item.description || '',
                  scadaReferenceName: item.scadaReferenceName || '',
                }));

                console.log('🔍 TASK by-plant processed data:', {
                  items: taskData.length,
                });
              }

              const combinedData = [...scadaData];

              console.log('🔍 FINAL RESULT by-plant:', {
                scadaData: scadaData.length,
                scadaTotal,
                taskData: taskData.length,
                combinedData: combinedData.length,
                combinedDataData: combinedData,
                total: scadaTotal + taskData.length,
              });

              return {
                data: combinedData,
                total: scadaTotal + taskData.length,
              };
            } catch (error) {
              console.error('🔍 ERROR Plant Filter:', error);
              throw error;
            }
          } else {
            console.log('🔍 Loading all variables (SCADA only)');
            response = await api.variablesScadaList({
              page,
              pageSize: perPage,
              SearchString: q,
            });
          }
          break;
        case 'zones':
          if (filters.plantId) {
            response = await api.zonesByPlantDetail(Number(filters.plantId));
            const data = Array.isArray(response.data) ? response.data : [];

            // Retorno directo para casos especiales
            return {
              data: data,
              total: data.length,
            };
          } else {
            response = await api.zonesList({
              page,
              pageSize: perPage,
              isPaginated: true,
              SearchString: q,
            });
          }
          break;
        case 'tables':
          if (filters.zoneId) {
            response = await api.tablesByZoneDetail(Number(filters.zoneId));
            const data = Array.isArray(response.data) ? response.data : [];

            // Retorno directo para casos especiales
            return {
              data: data,
              total: data.length,
            };
          } else {
            response = await api.tablesList({
              page,
              pageSize: perPage,
              isPaginated: true,
              SearchString: q,
            });
          }
          break;
        case 'tickets-admin':
          response = await api.ticketsAdminList({
            page,
            pageSize: perPage,
            ...filters,
            SearchText: q,
          });
          break;
        case 'notifications-admin':
          response = await api.notificationsAdminList({
            page,
            pageSize: perPage,
            ...filters,
          });
          break;
        case 'Subscripciones': {
          const subscriptionParams: any = {
            page,
            pageSize: perPage,
            ...(q ? { SearchString: q } : {}),
            ...(filters.PlantId ? { PlantId: Number(filters.PlantId) } : {}),
            ...(filters.ScadaReferenceName
              ? { SearchString: filters.ScadaReferenceName }
              : {}),
            ...(filters.UnitChange ? { UnitChange: filters.UnitChange } : {}),
            ...(filters.DataSourceType
              ? { DataSourceType: filters.DataSourceType }
              : {}),
            ...(filters.InstantEnabled !== undefined
              ? {
                  InstantEnabled:
                    filters.InstantEnabled === 'true' ||
                    filters.InstantEnabled === true,
                }
              : {}),
            ...(filters.InstantCalculationType
              ? { InstantCalculationType: filters.InstantCalculationType }
              : {}),
            ...(filters.InstantExpression
              ? { InstantExpression: filters.InstantExpression }
              : {}),
          };

          console.log(
            '🎯 [DataProvider] Parámetros para Subscripciones:',
            subscriptionParams
          );

          try {
            const subscriptionResponse =
              await getApiSubscriptionSources(subscriptionParams);

            console.log(
              '🎯 Subscripciones - Respuesta recibida:',
              subscriptionResponse
            );

            // La API devuelve void según el schema, intentemos obtener datos del axios response
            // Asumiendo que la respuesta real tiene datos aunque el tipo sea void
            const rawResponse: any = subscriptionResponse;

            let items = [];
            let totalCount = 0;

            if (rawResponse?.data?.items) {
              items = rawResponse.data.items;
              totalCount = rawResponse.data.totalCount || 0;
            } else if (Array.isArray(rawResponse?.data)) {
              items = rawResponse.data;
              totalCount = items.length;
            } else if (rawResponse?.items) {
              items = rawResponse.items;
              totalCount = rawResponse.totalCount || 0;
            }

            console.log('✅ Subscripciones - Datos procesados:', {
              items: items.length,
              total: totalCount,
            });

            return { data: items, total: totalCount };
          } catch (orvalError) {
            console.error('❌ Error en getList Subscripciones:', orvalError);
            throw orvalError;
          }
        }
        default:
          throw new Error(`Recurso no soportado: ${resource}`);
      }

      // BLOQUE DE EXTRACCIÓN
      let items = [];
      let totalCount = 0;

      if (response.data?.data?.items) {
        items = response.data.data.items;
        totalCount = response.data.data.totalCount || 0;
      } else if (response.data?.items) {
        items = response.data.items;
        totalCount = response.data.totalCount || 0;
      } else if (Array.isArray(response.data)) {
        items = response.data;
        totalCount = items.length;
      }

      return { data: items, total: totalCount };
    } catch (error) {
      console.error(`Error al obtener lista de ${resource}:`, error);
      throw error;
    }
  },

  getOne: async (resource, params) => {
    try {
      let response: any;

      switch (resource) {
        case 'inputs':
          response = await api.variablesInputDetail(Number(params.id));
          break;
        case 'task':
          try {
            const orvalResponse = await getApiVariablesTasks(Number(params.id));
            return { data: orvalResponse.data };
          } catch (orvalError) {
            console.error('❌ Orval - Error en getOne task:', orvalError);
            // Fallback si es necesario
            const fallbackResponse = await api.variablesTaskDetail(
              Number(params.id)
            );
            return { data: fallbackResponse.data };
          }
        case 'devices':
          try {
            const orvalResponse = await getApiVariablesDevices(
              Number(params.id)
            );
            return { data: orvalResponse.data };
          } catch (orvalError) {
            console.error('❌ Orval - Error en getOne device:', orvalError);
            console.log('🔄 Fallback a implementación original...');
            const fallbackResponse = await api.variablesDeviceDetail(
              Number(params.id)
            );
            return { data: fallbackResponse.data };
          }
          response = await api.variablesDeviceDetail(Number(params.id));
          break;
        case 'users':
          response = await api.usersDetail(String(params.id));
          break;
        case 'roles':
          response = await api.roleList();
          const role = response.data.find(
            (r: any) => r.id.toString() === params.id.toString()
          );
          if (!role) throw new Error('Rol no encontrado');
          return { data: role };
        case 'clients':
          response = await api.clientsDetail(Number(params.id));
          break;
        case 'plants':
          response = await api.plantsDetail(Number(params.id));
          break;
        case 'dashboardgroups':
          response = await api.dashboardGroupsDetail(Number(params.id));
          break;
        case 'dashboards':
          response = await api.dashboardsDetail(Number(params.id));
          break;
        case 'diagrams':
          response = await api.diagramsDetail(Number(params.id), {
            include: 'nodes,edges',
          });
          break;
        case 'alerts':
          response = await api.alertsDetail(Number(params.id));
          break;
        case 'variables':
          response = await api.variablesScadaDetail(Number(params.id));
          break;
        case 'zones':
          response = await api.zonesDetail(Number(params.id));
          break;
        case 'tables':
          response = await api.tablesDetail(Number(params.id));
          break;
        case 'role-permissions':
          response = await api.rolePermissionsByRoleDetail(String(params.id), {
            needsEntity: true,
          });
          return {
            data: {
              id: params.id,
              roleId: params.id,
              rolePermissions: response.data?.rolePermissions || [],
              users: response.data?.users || [],
            },
          };
        case 'tickets-admin':
        case 'tickets':
          response = await api.ticketsDetail(Number(params.id));
          break;
        case 'notifications-admin':
        case 'notifications':
          response = await api.notificationsDetail(Number(params.id));
          break;
        case 'Subscripciones':
          try {
            console.log('🎯 [DataProvider] getOne Subscripción:', params.id);
            const subscriptionResponse: any = await getApiSubscriptionSourcesId(
              Number(params.id)
            );
            console.log('✅ Subscripción obtenida:', subscriptionResponse);
            return { data: subscriptionResponse?.data || subscriptionResponse };
          } catch (error) {
            console.error('❌ Error al obtener Subscripción:', error);
            throw error;
          }
        default:
          throw new Error(`Recurso no soportado: ${resource}`);
      }
      return { data: response.data };
    } catch (error) {
      console.error(`Error al obtener ${resource} con ID ${params.id}:`, error);
      throw error;
    }
  },

  getMany: async (resource, params) => {
    try {
      const promises = params.ids.map(id =>
        dataProvider.getOne(resource, { id })
      );
      const results = await Promise.all(promises);
      return { data: results.map(r => r.data) };
    } catch (error) {
      console.error(`Error al obtener múltiples ${resource}:`, error);
      throw error;
    }
  },

  getManyReference: async (resource, params) => {
    try {
      const filterWithRef = { ...params.filter, [params.target]: params.id };
      return await dataProvider.getList(resource, {
        ...params,
        filter: filterWithRef,
      });
    } catch (error) {
      console.error(`Error al obtener referencia ${resource}:`, error);
      throw error;
    }
  },

  create: async (resource, params) => {
    try {
      let response: any;
      switch (resource) {
        case 'inputs':
          response = await api.variablesInputCreate(params.data);
          break;
        case 'devices':
          try {
            const orvalResponse = await postApiVariablesDevice(params.data);
            return { data: orvalResponse.data };
          } catch (orvalError) {
            console.error('❌ Orval - Error en create device:', orvalError);
            console.log('🔄 Fallback a implementación original...');
            const fallbackResponse = await api.variablesDeviceCreate(
              params.data
            );
            return { data: fallbackResponse.data };
          }
          response = await api.variablesDeviceCreate(params.data);
          break;
        case 'users':
          response = await api.usersCreate(params.data);
          break;
        case 'roles':
          response = await api.roleCreate(params.data);
          break;
        case 'clients':
          response = await api.clientsCreate(params.data);
          break;
        case 'plants':
          response = await api.plantsCreate(params.data);
          break;
        case 'dashboardgroups':
          response = await api.dashboardGroupsCreate(params.data);
          break;
        case 'dashboards':
          response = await api.dashboardsCreate(params.data);
          break;
        case 'diagrams':
          response = await api.diagramsCreate(params.data);
          break;
        case 'alerts':
          response = await api.alertsCreate(params.data);
          break;
        case 'variables':
          const ignoreWarnings = params.meta?.ignoreWarnings || false;
          console.log('🔍 [DataProvider] Creando variable SCADA con ignoreWarnings:', ignoreWarnings);
          try {
            // Usar método generado por orval con soporte para query params
            const scadaResult = await postApiVariablesScada(
              params.data,
              { ignoreWarnings }
            );
            console.log('🔍 [DataProvider] Resultado de orval:', scadaResult);
            // Verificar si el backend devolvió success: false (HTTP 200 pero error lógico)
            if (scadaResult && (scadaResult as any).success === false) {
              console.log('🔍 [DataProvider] Detectado success: false');
              const error: any = new Error(
                (scadaResult as any).errors?.[0]?.description || 'Error al crear la variable'
              );
              error.body = scadaResult;
              throw error;
            }
            response = { data: scadaResult };
          } catch (scadaError: any) {
            console.log('🔍 [DataProvider] Error capturado:', scadaError);
            console.log('🔍 [DataProvider] response.data:', scadaError.response?.data);
            // Extraer el body del error de axios (HTTP 400)
            const errorBody = scadaError.response?.data || scadaError.body;
            const errorMessage = errorBody?.errors?.[0]?.description ||
                                 scadaError.message ||
                                 'Error al crear la variable';
            console.log('🔍 [DataProvider] errorMessage:', errorMessage);
            const customError: any = new Error(errorMessage);
            customError.body = errorBody;
            throw customError;
          }
          break;
        case 'zones':
          response = await api.zonesCreate(params.data);
          break;
        case 'tables':
          response = await api.tablesCreate(params.data);
          break;
        case 'notifications':
        case 'notifications-admin':
          const notificationData = { ...params.data };
          if (notificationData.expiresAt) {
            if (notificationData.expiresAt instanceof Date) {
              notificationData.expiresAt =
                notificationData.expiresAt.toISOString();
            } else if (typeof notificationData.expiresAt === 'string') {
              let dateString = notificationData.expiresAt;
              if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/))
                dateString += ':00';
              if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/))
                dateString += '.000';
              if (
                !dateString.endsWith('Z') &&
                !dateString.match(/[+-]\d{2}:\d{2}$/)
              )
                dateString += 'Z';
              const date = new Date(dateString);
              if (!isNaN(date.getTime()))
                notificationData.expiresAt = date.toISOString();
            }
          }
          response = await api.notificationsCreate(notificationData as any);
          break;
        case 'tickets':
        case 'tickets-admin':
          const ticketData = {
            title: params.data.title,
            description: params.data.description,
            category: params.data.category,
            priority: params.data.priority || 1,
          };
          response = await api.ticketsCreate(ticketData as any);
          break;
        case 'Subscripciones':
          try {
            console.log('🎯 [DataProvider] Crear Subscripción:', params.data);
            const subscriptionResponse = await postApiSubscriptionSources(
              params.data
            );
            console.log('✅ Subscripción creada:', subscriptionResponse);
            // El response puede ser void, pero retornamos el data enviado con un ID generado
            return { data: { ...params.data, id: Date.now() } };
          } catch (error) {
            console.error('❌ Error al crear Subscripción:', error);
            throw error;
          }
        default:
          throw new Error(`Recurso no soportado: ${resource}`);
      }

      let result;
      if (response.data?.data) {
        result = response.data.data;
      } else {
        result = response.data;
      }
      return { data: result };
    } catch (error) {
      console.error(`Error al crear ${resource}:`, error);
      throw error;
    }
  },

  update: async (resource, params) => {
    try {
      let response: any;
      switch (resource) {
        case 'inputs':
          response = await api.variablesInputUpdate(params.data);
          break;
        case 'devices':
          try {
            const orvalResponse = await putApiVariablesDevice(
              Number(params.id),
              params.data
            );
            return { data: orvalResponse.data };
          } catch (orvalError) {
            console.error('❌ Orval - Error en update device:', orvalError);
            console.log('🔄 Fallback a implementación original...');
            const fallbackResponse = await api.variablesDeviceUpdate(
              params.data
            );
            return { data: fallbackResponse.data };
          }
          response = await api.variablesDeviceUpdate(params.data);
          break;
        case 'users':
          response = await api.usersUpdate(String(params.id), params.data);
          break;
        case 'roles':
          response = await api.roleUpdate(String(params.id), {
            id: String(params.id),
            name: params.data.name,
          });
          break;
        case 'clients':
          response = await api.clientsUpdate(Number(params.id), params.data);
          break;
        case 'plants':
          response = await api.plantsUpdate(params.data, {
            id: Number(params.id),
          });
          break;
        case 'dashboardgroups':
          response = await api.dashboardGroupsUpdate(
            Number(params.id),
            params.data
          );
          break;
        case 'dashboards':
          response = await api.dashboardsUpdate(Number(params.id), params.data);
          break;
        case 'diagrams':
          response = await api.diagramsUpdate(params.data);
          break;
        case 'alerts':
          response = await api.alertsUpdate({
            ...params.data,
            id: Number(params.id),
          });
          break;
        case 'variables':
          response = await api.variablesScadaUpdate(params.data);
          break;
        case 'zones':
          response = await api.zonesUpdate(Number(params.id), params.data);
          break;
        case 'tables':
          response = await api.tablesUpdate(Number(params.id), params.data);
          break;
        case 'tickets':
        case 'tickets-admin':
          if (
            params.data.status &&
            !params.data.title &&
            !params.data.description
          ) {
            response = await api.ticketsStatusUpdate(Number(params.id), {
              status: params.data.status,
              resolutionNotes: params.data.resolutionNotes,
              assignedToUserId: params.data.assignedToUserId,
            });
          } else {
            const ticketData = {
              title: params.data.title,
              description: params.data.description,
              category: params.data.category,
              priority: params.data.priority,
            };
            response = await api.ticketsUpdate(
              Number(params.id),
              ticketData as any
            );
          }
          break;
        case 'notifications':
        case 'notifications-admin':
          if (
            !params.data.title &&
            !params.data.content &&
            Object.keys(params.data).length <= 1
          ) {
            response = await api.notificationsMarkReadCreate(Number(params.id));
          } else {
            const notificationData = { ...params.data };
            if (notificationData.expiresAt) {
              if (notificationData.expiresAt instanceof Date) {
                notificationData.expiresAt =
                  notificationData.expiresAt.toISOString();
              } else if (typeof notificationData.expiresAt === 'string') {
                let dateString = notificationData.expiresAt;
                if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/))
                  dateString += ':00';
                if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/))
                  dateString += '.000';
                if (
                  !dateString.endsWith('Z') &&
                  !dateString.match(/[+-]\d{2}:\d{2}$/)
                )
                  dateString += 'Z';
                const date = new Date(dateString);
                if (!isNaN(date.getTime()))
                  notificationData.expiresAt = date.toISOString();
              }
            }
            response = await api.notificationsUpdate(
              Number(params.id),
              notificationData as any
            );
          }
          break;
        case 'Subscripciones':
          try {
            console.log('🎯 [DataProvider] Actualizar Subscripción:', {
              id: params.id,
              data: params.data,
            });
            const subscriptionResponse = await putApiSubscriptionSourcesId(
              Number(params.id),
              params.data
            );
            console.log('✅ Subscripción actualizada:', subscriptionResponse);
            return { data: subscriptionResponse?.data || params.data };
          } catch (error) {
            console.error('❌ Error al actualizar Subscripción:', error);
            throw error;
          }
        default:
          throw new Error(`Recurso no soportado: ${resource}`);
      }
      return { data: response.data };
    } catch (error) {
      console.error(
        `Error al actualizar ${resource} con ID ${params.id}:`,
        error
      );
      throw error;
    }
  },

  updateMany: async (resource, params) => {
    try {
      const promises = params.ids.map(id =>
        dataProvider.update(resource, {
          id,
          data: params.data,
          previousData: params.data,
        })
      );
      const results = await Promise.all(promises);
      return { data: results.map(r => r.data.id) };
    } catch (error) {
      console.error(`Error al actualizar múltiples ${resource}:`, error);
      throw error;
    }
  },

  delete: async (resource, params) => {
    try {
      const pass: string | undefined = (params as any)?.meta?.pass;
      switch (resource) {
        case 'inputs':
          await api.variablesDelete({ id: Number(params.id) });
          return { data: params.previousData ?? { id: params.id } };
        case 'devices':
          try {
            await deleteApiVariablesDevice(Number(params.id));
            return { data: params.previousData ?? { id: params.id } };
          } catch (orvalError) {
            console.error('❌ Orval - Error en delete device:', orvalError);
            await api.variablesDeviceDelete({ id: Number(params.id) });
            return { data: params.previousData ?? { id: params.id } };
          }
          await api.variablesDeviceDelete({ id: Number(params.id) });
          return { data: params.previousData ?? { id: params.id } };
        case 'users':
          await api.usersDelete(String(params.id));
          break;
        case 'roles':
          await api.roleDelete(String(params.id));
          break;
        case 'clients':
          await api.clientsDelete(Number(params.id), { pass });
          break;
        case 'plants':
          await api.plantsDelete({ id: Number(params.id), pass });
          break;
        case 'dashboardgroups':
          await api.dashboardGroupsDelete(Number(params.id));
          break;
        case 'dashboards':
          await api.dashboardsDelete(Number(params.id));
          break;
        case 'diagrams':
          await api.diagramsDelete(Number(params.id));
          break;
        case 'alerts':
          await api.alertsDelete(Number(params.id));
          break;
        case 'variables':
          await api.variablesDelete({ id: Number(params.id) });
          break;
        case 'zones':
          await api.zonesDelete(Number(params.id), { pass });
          break;
        case 'tables':
          await api.tablesDelete(Number(params.id), { pass });
          break;
        case 'notifications':
        case 'notifications-admin':
          await api.notificationsPermanentDelete(Number(params.id));
          break;
        case 'tickets':
        case 'tickets-admin':
          await api.ticketsDelete(Number(params.id));
          break;
        case 'task':
          try {
            await deleteApiVariablesDevice(Number(params.id));
            return { data: params.previousData ?? { id: params.id } };
          } catch (orvalError) {
            console.error('❌ Orval - Error en delete task:', orvalError);
            await api.variablesTaskDelete({ id: Number(params.id) });
            return { data: params.previousData ?? { id: params.id } };
          }
        case 'Subscripciones':
          try {
            console.log('🎯 [DataProvider] Eliminar Subscripción:', params.id);
            await deleteApiSubscriptionSourcesId(Number(params.id), {
              deleteDataSource: true,
            });
            console.log('✅ Subscripción eliminada');
            return { data: params.previousData ?? { id: params.id } };
          } catch (error) {
            console.error('❌ Error al eliminar Subscripción:', error);
            throw error;
          }
        default:
          throw new Error(`Recurso no soportado: ${resource}`);
      }
      return { data: (params.previousData || { id: params.id }) as any };
    } catch (error) {
      console.error(
        `Error al eliminar ${resource} con ID ${params.id}:`,
        error
      );
      throw error;
    }
  },

  deleteMany: async (resource, params) => {
    try {
      const promises = params.ids.map(id =>
        dataProvider.delete(resource, { id, previousData: {} as any })
      );
      await Promise.all(promises);
      return { data: params.ids };
    } catch (error) {
      console.error(`Error al eliminar múltiples ${resource}:`, error);
      throw error;
    }
  },
};
